import { useMemo, type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import type { GlobalState } from '@/store';
import { menuItems } from '@/config/menu';

export interface AppRoute {
  key: string;
  name: string;
  path: string;
  /** 无 permission 表示登录即可访问（如个人中心） */
  permission?: string;
}

export const PROFILE_PATH = '/profile';

export const appRoutes: AppRoute[] = [
  { key: 'profile', name: 'menu.profile', path: PROFILE_PATH },
  { key: 'dashboard', name: 'menu.dashboard', path: '/dashboard', permission: 'dashboard:view' },
  { key: 'inquiries', name: 'menu.inquiries', path: '/inquiries', permission: 'inquiries:view' },
  { key: 'downloads', name: 'menu.downloads', path: '/downloads', permission: 'downloads:view' },
  { key: 'newsletters', name: 'menu.newsletters', path: '/newsletters', permission: 'newsletters:view' },
  { key: 'users', name: 'menu.users', path: '/users', permission: 'users:view' },
  { key: 'roles', name: 'menu.roles', path: '/roles', permission: 'roles:view' },
  { key: 'news', name: 'menu.news', path: '/news', permission: 'news:view' },
  { key: 'newsCreate', name: 'menu.news', path: '/news/create', permission: 'news:create' },
  { key: 'newsEdit', name: 'menu.news', path: '/news/edit/:id', permission: 'news:edit' },
  { key: 'masterDataBd', name: 'menu.bdOwners', path: '/master-data/bd', permission: 'bd-owners:view' },
  { key: 'operationLogs', name: 'menu.operationLogs', path: '/operation-logs', permission: 'logs:view' },
  { key: 'help', name: 'menu.help', path: '/help' },
];

export function resolveLandingPath(permissions: string[] = []): string {
  const first = appRoutes.find(
    (route) => route.path !== PROFILE_PATH && (!route.permission || permissions.includes(route.permission)),
  );
  return first?.path ?? PROFILE_PATH;
}

export function useLandingPath(): string {
  const user = useSelector((state: GlobalState) => state.user);
  return useMemo(() => resolveLandingPath(user?.permissions ?? []), [user?.permissions]);
}

export function useVisibleRoutes(): AppRoute[] {
  const user = useSelector((state: GlobalState) => state.user);
  const permissions = user?.permissions ?? [];

  return useMemo(
    () =>
      appRoutes.filter((route) => !route.permission || permissions.includes(route.permission)),
    [permissions],
  );
}

export function useVisibleMenuItems() {
  const user = useSelector((state: GlobalState) => state.user);
  const permissions = user?.permissions ?? [];

  return useMemo(
    () =>
      menuItems.filter((item) => !item.permission || permissions.includes(item.permission)),
    [permissions],
  );
}

interface PermissionRouteProps {
  permission?: string;
  children: ReactNode;
}

export function PermissionRoute({ permission, children }: PermissionRouteProps) {
  const user = useSelector((state: GlobalState) => state.user);
  const permissions = user?.permissions ?? [];

  if (permission && !permissions.includes(permission)) {
    return <Navigate to={PROFILE_PATH} replace />;
  }

  return <>{children}</>;
}
